import { Badge, Box, Table } from '@palmetto/palmetto-components';
import type { BadgeVariant, Column } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState, type CSSProperties } from 'react';

import { CustomerPaymentLink } from 'types';

import { DeactivatePaymentLinkModal } from './DeactivatePaymentLinkModal';
import { PaymentLinkActionMenu } from './PaymentLinkActionMenu';

interface CustomerPaymentLinkHistoryProps {
  links: CustomerPaymentLink[];
}

const TABLE_WRAPPER_STYLE: CSSProperties = { maxHeight: '490px', overflowY: 'auto', width: '100%' };
const EMPTY_LINKS: CustomerPaymentLink[] = [];
const DATE_CELL_STYLE: CSSProperties = { display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap' };
const WRAP_CELL_STYLE: CSSProperties = { overflowWrap: 'anywhere', wordBreak: 'break-word' };
const TRUNCATE_CELL_STYLE: CSSProperties = {
  display: 'block',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
function renderDateTime(date: Date | string) {
  const dt = DateTime.fromJSDate(new Date(date)).toLocal();
  return (
    <Box style={DATE_CELL_STYLE}>
      <span>{dt.toFormat('LLL d, yyyy')}</span>
      <span>{dt.toFormat('h:mm a')}</span>
    </Box>
  );
}

type LinkStatus = 'Completed' | 'Active' | 'Deactivated' | 'Expired';

function getLinkStatus(link: CustomerPaymentLink): LinkStatus {
  if (link.linkSuccessfullyUsedAt) return 'Completed';
  if (link.deactivatedAt) return 'Deactivated';
  return new Date(link.expiresAt) > new Date() ? 'Active' : 'Expired';
}

const STATUS_VARIANT: Record<LinkStatus, BadgeVariant> = {
  Completed: 'success',
  Active: 'info',
  Deactivated: 'warning',
  Expired: 'default',
};

export function CustomerPaymentLinkHistory({ links }: CustomerPaymentLinkHistoryProps) {
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const openDeactivateModal = useCallback(() => setIsDeactivateModalOpen(true), []);
  const closeDeactivateModal = useCallback(() => setIsDeactivateModalOpen(false), []);

  const rows = useMemo(
    () =>
      (links ?? EMPTY_LINKS)
        .slice()
        .toSorted((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
        .map((l, i) =>
          Object.assign({}, l, { _key: `${new Date(l.sentAt).getTime()}-${i}`, status: getLinkStatus(l) }),
        ),
    [links],
  );

  const columns = useMemo<Column[]>(
    () => [
      {
        heading: 'DATE SENT',
        dataKey: 'sentAt',
        width: 130,
        render: (cell?: unknown) => renderDateTime(cell as Date),
      },
      {
        heading: 'SENT TO',
        dataKey: 'sentToEmail',
        render: (_cell?: unknown, row?: Record<string, unknown>) => {
          const email = row?.sentToEmail as string | undefined;
          const phone = row?.sentToPhone as string | undefined;
          const parts = [email, phone].filter(Boolean) as string[];
          if (parts.length === 0) {
            // Link-only generations record no recipient (deliveryMethods: []) — the link
            // was produced for manual distribution, not sent to the customer.
            const deliveryMethods = row?.deliveryMethods as string[] | undefined;
            return Array.isArray(deliveryMethods) && deliveryMethods.length === 0 ? 'Link Only' : '—';
          }
          return (
            <Box style={WRAP_CELL_STYLE} childGap="2xs">
              {parts.map((p) => (
                <span key={p}>{p}</span>
              ))}
            </Box>
          );
        },
      },
      {
        heading: 'SENT BY',
        dataKey: 'sentByEmail',
        render: (cell?: unknown) => {
          const value = cell as string | undefined;
          return value ? (
            <span title={value} style={TRUNCATE_CELL_STYLE}>
              {value}
            </span>
          ) : (
            '—'
          );
        },
      },
      {
        heading: 'EXPIRES',
        dataKey: 'expiresAt',
        width: 130,
        render: (cell?: unknown) => renderDateTime(cell as Date),
      },
      {
        heading: 'LAST OPENED',
        dataKey: 'lastOpenedAt',
        width: 130,
        render: (cell?: unknown) => (cell ? renderDateTime(cell as Date) : '—'),
      },
      {
        heading: 'STATUS',
        dataKey: 'status',
        width: 110,
        render: (cell?: unknown) => {
          const status = cell as LinkStatus;
          return <Badge variant={STATUS_VARIANT[status]} message={status} />;
        },
      },
      {
        heading: 'ACTION',
        dataKey: 'link',
        width: 100,
        align: 'center',
        render: (cell?: unknown, row?: Record<string, unknown>) => (
          <PaymentLinkActionMenu
            link={cell as string | undefined}
            isActive={row?.status === 'Active'}
            onDeactivate={openDeactivateModal}
          />
        ),
      },
    ],
    [openDeactivateModal],
  );

  return (
    <Box childGap="md" margin="xl 0 0 0">
      <Box as="h3" fontSize="md" fontWeight="bold">
        PAYMENT LINK HISTORY
      </Box>
      <Box style={TABLE_WRAPPER_STYLE}>
        <Table rowKey="_key" columns={columns} rows={rows} useFixedTableLayout hasStickyHeader />
      </Box>
      <DeactivatePaymentLinkModal isOpen={isDeactivateModalOpen} onClose={closeDeactivateModal} />
    </Box>
  );
}
